import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { colors } from '../../utils/colors';
import { Container, SectionTitle } from '../common';

const TestimonialsSection = styled.section`
  padding: 6rem 1rem 3rem; /* Reduced bottom padding from 6rem to 3rem */
  background: ${colors.backgroundLight};
  overflow: hidden;
`;

const CarouselContainer = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
`;

const CardsContainer = styled.div`
  position: relative;
  height: 240px; /* Fixed height for the cards area */
  margin-bottom: 1rem; /* Reduced from 2rem */
  
  @media (max-width: 768px) {
    height: 380px; /* Fixed height for tablets */
    margin-bottom: 0.5rem;
  }
  
  @media (max-width: 480px) {
    height: 420px; /* Fixed height for mobile */
    margin-bottom: 0.25rem;
  }
`;

const TestimonialCard = styled(motion.div)`
  background: ${colors.white};
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 4px 20px ${colors.shadow};
  text-align: center;
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.25rem;
  }
`;

const Quote = styled.div`
  color: ${colors.textLight};
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  font-style: italic;

  &::before,
  &::after {
    content: '"';
    color: ${colors.primary};
    font-size: 1.5em;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.5;
    margin-bottom: 1.25rem;
  }
`;

const Author = styled.div`
  h4 {
    color: ${colors.textDark};
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: ${colors.primary};
    font-size: 0.9rem;
  }
`;

const CarouselDots = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    margin-top: -1rem;
  }
  
  @media (max-width: 480px) {
    margin-top: -7rem;
  }
`;

const Dot = styled.button.withConfig({
  shouldForwardProp: (prop) => !['active'].includes(prop)
})`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? colors.primary : colors.border};
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    background: ${props => props.active ? colors.primary : colors.primaryLight};
  }
`;

const testimonials = [
  {
    id: 1,
    quote: "Las técnicas cognitivo-conductuales me ayudaron a cambiar esos pensamientos negativos que me tenían paralizada. Ahora tengo herramientas concretas y recuperé mi confianza.",
    author: "Laura M.",
    role: "Terapia individual y manejo de ansiedad"
  },
  {
    id: 2,
    quote: "El acompañamiento durante mi duelo fue fundamental. Me dio un espacio seguro para sanar. Las sesiones online me facilitaron conectar desde casa cuando no tenía fuerzas.",
    author: "Roberto C.",
    role: "Acompañamiento en duelos - Modalidad online"
  },
  {
    id: 3,
    quote: "El EMDR cambió mi vida. Tenía pesadillas por un accidente y ahora esos recuerdos ya no me controlan. Es increíble cómo funciona esta terapia.",
    author: "Ana V.",
    role: "Terapia con EMDR"
  },
  {
    id: 4,
    quote: "Las sesiones nos dieron herramientas para comunicarnos mejor con mi hijo adolescente. Está más tranquilo, habla más con nosotros y volvió a interesarse por sus estudios.",
    author: "Patricia y familia",
    role: "Terapia individual para adolescentes"
  },
  {
    id: 5,
    quote: "Aprendí técnicas de respiración y a identificar mis disparadores de ansiedad. Ahora manejo mucho mejor mis emociones y volví a hacer cosas que había dejado.",
    author: "Sofía R.",
    role: "Manejo de ansiedad y desarrollo personal"
  }
];

export const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection) => {
    setDirection(newDirection);
    setCurrentIndex((prevIndex) => {
      const nextIndex = prevIndex + newDirection;
      if (nextIndex < 0) return testimonials.length - 1;
      if (nextIndex >= testimonials.length) return 0;
      return nextIndex;
    });
  };

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      paginate(1);
    }, 10000);

    return () => clearInterval(timer);
  }, []);

  return (
    <TestimonialsSection id="testimonios" aria-label="Testimonios de pacientes">
      <Container>
        <SectionTitle
          title="Lo que dicen mis pacientes"
          subtitle="Experiencias reales de personas que han encontrado apoyo y crecimiento a través de la terapia."
          centered
        />

        <CarouselContainer>
          <CardsContainer>
            <AnimatePresence initial={false} custom={direction}>
              <TestimonialCard
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={1}
                onDragEnd={(e, { offset, velocity }) => {
                  const swipe = swipePower(offset.x, velocity.x);

                  if (swipe < -swipeConfidenceThreshold) {
                    paginate(1);
                  } else if (swipe > swipeConfidenceThreshold) {
                    paginate(-1);
                  }
                }}
                role="group"
                aria-roledescription="slide"
                aria-label={`Testimonio ${currentIndex + 1} de ${testimonials.length}`}
              >
                <Quote>{testimonials[currentIndex].quote}</Quote>
                <Author>
                  <h4>{testimonials[currentIndex].author}</h4>
                  <p>{testimonials[currentIndex].role}</p>
                </Author>
              </TestimonialCard>
            </AnimatePresence>
          </CardsContainer>

          <CarouselDots role="tablist" aria-label="Navegación de testimonios">
            {testimonials.map((_, index) => (
              <Dot
                key={index}
                active={currentIndex === index}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                }}
                aria-selected={currentIndex === index}
                aria-label={`Ir al testimonio ${index + 1}`}
                role="tab"
              />
            ))}
          </CarouselDots>
        </CarouselContainer>
      </Container>
    </TestimonialsSection>
  );
};